import { apiClient } from './apiClient';
import { listStaffUsers } from './staffService';

import type { ApiResponse } from '@/types/auth.types';
import type {
  ApiBookingData,
  ApiBookingListResponse,
  ApiBulkCreateResponse,
  ApiSlotData,
  ApiSlotListResponse,
  BookingListItem,
  BulkCreateParams,
  CreateSlotParams,
  InterviewSlot,
  Interviewer,
  OutcomeType,
  SlotBooking,
  UpdateSlotParams,
} from '@/types/scheduling.types';

// ── Default timezone (used for create/bulk-create) ──────────────────────

const DEFAULT_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

// ── Helpers ─────────────────────────────────────────────────────────────

function mapSlot(slot: ApiSlotData): InterviewSlot {
  return {
    slotId: slot.slotId,
    interviewerUserId: slot.interviewer.staffUserId,
    interviewerName: `${slot.interviewer.firstName} ${slot.interviewer.lastName}`,
    startAtUtc: slot.startAtUtc,
    endAtUtc: slot.endAtUtc,
    capacity: slot.capacity,
    notes: slot.notes,
    bookings: [],
    createdAt: slot.createdAt,
  };
}

/**
 * Convert JS day-of-week (0=Sun..6=Sat) to ISO (1=Mon..7=Sun)
 */
function jsToIsoDayOfWeek(jsDay: number): number {
  return jsDay === 0 ? 7 : jsDay;
}

// ── Public API ──────────────────────────────────────────────────────────

export async function listInterviewers(): Promise<Interviewer[]> {
  const staff = await listStaffUsers();
  return staff.map((s) => ({
    userId: s.userId,
    name: `${s.firstName} ${s.lastName}`,
  }));
}

export async function listSlots(from: string, to: string): Promise<InterviewSlot[]> {
  const fromDate = new Date(from);
  const toDate = new Date(to);
  const fromStr = `${fromDate.getFullYear()}-${String(fromDate.getMonth() + 1).padStart(2, '0')}-${String(fromDate.getDate()).padStart(2, '0')}`;
  const toStr = `${toDate.getFullYear()}-${String(toDate.getMonth() + 1).padStart(2, '0')}-${String(toDate.getDate()).padStart(2, '0')}`;

  const response = await apiClient.get<ApiResponse<ApiSlotListResponse>>(
    `/scheduling/slots?from=${fromStr}&to=${toStr}&limit=100&sortBy=startAtUtc&sortOrder=asc`,
  );
  return response.data.items.map(mapSlot);
}

export async function createSlot(params: CreateSlotParams): Promise<InterviewSlot> {
  const response = await apiClient.post<ApiResponse<ApiSlotData>>('/scheduling/slots', {
    interviewerUserId: params.interviewerUserId,
    tz: DEFAULT_TZ,
    startAtUtc: params.startAtUtc,
    endAtUtc: params.endAtUtc,
    capacity: params.capacity,
    notes: params.notes,
  });
  return mapSlot(response.data);
}

export async function updateSlot(slotId: string, params: UpdateSlotParams): Promise<InterviewSlot> {
  const body: Record<string, unknown> = {};
  if (params.startAtUtc !== undefined) body['startAtUtc'] = params.startAtUtc;
  if (params.endAtUtc !== undefined) body['endAtUtc'] = params.endAtUtc;
  if (params.capacity !== undefined) body['capacity'] = params.capacity;
  if (params.notes !== undefined) body['notes'] = params.notes;

  const response = await apiClient.put<ApiResponse<ApiSlotData>>(`/scheduling/slots/${slotId}`, body);
  return mapSlot(response.data);
}

export async function deleteSlot(slotId: string): Promise<void> {
  await apiClient.delete(`/scheduling/slots/${slotId}`);
}

export async function bulkCreateSlots(params: BulkCreateParams): Promise<InterviewSlot[]> {
  const response = await apiClient.post<ApiResponse<ApiBulkCreateResponse>>('/scheduling/slots/bulk', {
    interviewerUserId: params.interviewerUserId,
    tz: DEFAULT_TZ,
    daysOfWeek: params.daysOfWeek.map(jsToIsoDayOfWeek),
    startDate: params.startDate,
    endDate: params.endDate,
    startTime: params.startTime,
    endTime: params.endTime,
    slotDurationMin: params.slotDurationMin,
    capacity: params.capacity,
  });
  return response.data.created.map(mapSlot);
}

// ── Bookings / Attendance ──────────────────────────────────────────────

function mapBooking(booking: ApiBookingData): BookingListItem {
  const interviewer = booking.slot?.interviewer;
  return {
    bookingId: booking.bookingId,
    applicationId: booking.applicationId,
    applicantName: booking.applicant ? `${booking.applicant.firstName} ${booking.applicant.lastName}` : 'Unknown',
    status: booking.status,
    startAtUtc: booking.slot?.startAtUtc ?? '',
    endAtUtc: booking.slot?.endAtUtc ?? '',
    interviewerName: interviewer ? `${interviewer.firstName} ${interviewer.lastName}` : '',
    interviewerUserId: interviewer?.staffUserId ?? '',
    notes: booking.notes,
    bookedAt: booking.bookedAt,
  };
}

export async function listBookingsForSlot(slotId: string): Promise<SlotBooking[]> {
  const response = await apiClient.get<ApiResponse<ApiBookingListResponse>>(
    `/scheduling/bookings?slotId=${slotId}&limit=100`,
  );
  return response.data.items.map((b) => ({
    bookingId: b.bookingId,
    applicationId: b.applicationId,
    applicantName: b.applicant ? `${b.applicant.firstName} ${b.applicant.lastName}` : 'Unknown',
    status: b.status,
    bookedAt: b.bookedAt,
  }));
}

export async function listBookings(from: string, to: string): Promise<BookingListItem[]> {
  const response = await apiClient.get<ApiResponse<ApiBookingListResponse>>(
    `/scheduling/bookings?from=${from}&to=${to}&limit=100&sortBy=bookedAt&sortOrder=asc`,
  );
  return response.data.items.map(mapBooking);
}

export async function markOutcome(bookingId: string, outcome: OutcomeType, notes?: string): Promise<void> {
  await apiClient.put(`/scheduling/bookings/${bookingId}/outcome`, { outcome, notes });
}

export async function downloadBookingIcs(bookingId: string): Promise<Blob> {
  let token: string | null = null;
  try {
    const stored = localStorage.getItem('kent-hr-auth');
    if (stored) {
      const parsed = JSON.parse(stored) as { state?: { accessToken?: string } };
      token = parsed.state?.accessToken ?? null;
    }
  } catch {
    // ignore parse errors
  }

  const response = await fetch(`/api/scheduling/bookings/${encodeURIComponent(bookingId)}/ics`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    throw new Error('Failed to download ICS file');
  }

  return response.blob();
}
